import React, { useContext, useMemo, useReducer, useLayoutEffect, createContext, PropsWithChildren } from 'react';
import { StorageContext } from '../providers';
import { UserPreferences, DEFAULT_PREFERENCES } from '../domain';
import { TsFixMeType } from '../types';
import { authenticate, socialAuth, signOut, onSessionEnd, onSessionReset } from '../services/auth-service/auth-service';
import AuthData from '../services/auth-service/auth-data';
import { getCurrentUser } from '../services/user-service';
import { hasValue } from '../utils/common-utils';
import * as AuthTypes from './types';
import authReducer, { initialAuthState } from './authentication-reducer';

export type UserData = TsFixMeType;

type LoginParams = {
  user: string;
  password: string;
  rememberMe: boolean;
};

export type SocialLoginParams = {
  socialToken: string;
  clientType: string;
  source: string;
  form: string;
};

export interface AuthenticationContextApi {
  authData: any;
  isAuthenticated: boolean;
  isStaff: boolean;
  user: UserData;
  logout: () => void;
  login: (params: LoginParams) => void;
  socialLogin: (params: SocialLoginParams) => Promise<{ userData: UserData; userExists: boolean }>;
}

export const AuthenticationContext = createContext<AuthenticationContextApi>({
  authData: {},
  isAuthenticated: false,
  user: {
    id: undefined as number | undefined,
    customer: undefined as TsFixMeType,
    username: null as string | null,
    preferences: DEFAULT_PREFERENCES,
    roles: [] as string[],
  },
} as AuthenticationContextApi);

export interface AuthenticationProviderProps {
  onAuthDataLoaded: (authData: TsFixMeType) => void;
  onLogout?: () => void;
  token?: string;
  refreshToken?: string;
}

export const AuthenticationProvider: React.FC<PropsWithChildren<AuthenticationProviderProps>> = ({
  children,
  onAuthDataLoaded,
  onLogout = () => {},
  token = '',
  refreshToken = '',
  ...rest
}) => {
  const storageDriver = useContext(StorageContext);
  const [authState, dispatch] = useReducer(authReducer, initialAuthState);

  // TODO: this is the way we have to load tokens from storage or from cookies (received in props)
  // independently of the storage driver
  // In a near future refactor we will probably move this initialization to
  // web and app
  useLayoutEffect(() => {
    if (!!token || !!refreshToken) {
      AuthData.set({ token, refresh_token: refreshToken });
    } else {
      storageDriver && AuthData.init(storageDriver);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadData = (cancel = false) => {
    Promise.all([AuthData.get(), getCurrentUser()])
      .then(([authData, userData]) => {
        if (cancel) {
          return;
        }

        if (authData) {
          dispatch({
            type: AuthTypes.AuthenticationProviderActionTypes.RESET,
            payload: authData,
          });
          onAuthDataLoaded(authData);
        } else {
          dispatch({
            type: AuthTypes.AuthenticationProviderActionTypes.RESET,
          });
        }

        hasValue(userData) &&
          dispatch({
            type: AuthTypes.AuthenticationProviderActionTypes.USER_DATA,
            payload: userData,
          });
      })
      .catch(error =>
        dispatch({
          type: AuthTypes.AuthenticationProviderActionTypes.ERROR,
          payload: error,
        }),
      )
      .then(() => UserPreferences.loadUserPreferences(storageDriver!))
      .then(
        (preferences: TsFixMeType) =>
          !cancel &&
          dispatch({
            type: AuthTypes.AuthenticationProviderActionTypes.PREFERENCES,
            payload: preferences,
          }),
      );
  };

  useLayoutEffect(() => {
    let cancel = false;
    loadData(cancel);

    const sessionEndCleanup = onSessionEnd(() =>
      dispatch({
        type: AuthTypes.AuthenticationProviderActionTypes.LOGOUT,
      }),
    );
    const sessionResetCleanup = onSessionReset(() => {
      dispatch({
        type: AuthTypes.AuthenticationProviderActionTypes.RELOAD,
      });
      loadData();
    });

    return () => {
      sessionEndCleanup();
      sessionResetCleanup();
      cancel = true;
    };
  }, []); //eslint-disable-line react-hooks/exhaustive-deps

  const authApi = useMemo(
    () => ({
      login: ({ user, password, rememberMe }: TsFixMeType) => {
        dispatch({
          type: AuthTypes.AuthenticationProviderActionTypes.START,
        });

        return authenticate({
          username: user,
          password,
          persist: rememberMe,
        })
          .then(data => {
            dispatch({
              type: AuthTypes.AuthenticationProviderActionTypes.LOGIN,
              payload: data,
            });
          })
          .catch(err => {
            dispatch({
              type: AuthTypes.AuthenticationProviderActionTypes.ERROR,
              payload: {
                status: err.status,
              },
            });
            throw err;
          })
          .then(() => getCurrentUser())
          .then((user: UserData) => {
            dispatch({
              type: AuthTypes.AuthenticationProviderActionTypes.USER_DATA,
              payload: user,
            });
            return user;
          });
      },
      socialLogin: ({ socialToken, clientType, source, form }: TsFixMeType) => {
        dispatch({
          type: AuthTypes.AuthenticationProviderActionTypes.START,
        });

        return socialAuth({ socialToken, clientType, source, form })
          .then(data => {
            dispatch({
              type: AuthTypes.AuthenticationProviderActionTypes.SOCIAL_LOGIN,
              payload: data.authData,
            });
            return data.userExists;
          })
          .catch(err => {
            dispatch({
              type: AuthTypes.AuthenticationProviderActionTypes.ERROR,
              payload: {
                status: err.status,
              },
            });
            throw err;
          })
          .then(userExists =>
            getCurrentUser().then((userData: UserData) => ({
              userData,
              userExists,
            })),
          )
          .then(({ userData, userExists }) => {
            dispatch({
              type: AuthTypes.AuthenticationProviderActionTypes.USER_DATA,
              payload: userData,
            });

            return {
              userData,
              userExists,
            };
          });
      },
      logout: () => {
        onLogout();
        signOut();
      },

      updateUserData: (newUserData: UserData) =>
        dispatch({
          type: AuthTypes.AuthenticationProviderActionTypes.USER_DATA,
          payload: newUserData,
        }),

      user: {
        ...authState.userData,
        roles: authState.userData ? authState.userData.roles : [],
        preferences: authState.preferences,
      } as UserData,
      authData: authState,
      isAuthenticated: Boolean(authState.token) || Boolean(authState.refresh_token),
      isStaff: authState.userData?.roles.includes('ROLE_STAFF') ?? false,
    }),
    [authState, onLogout],
  );

  return (
    <AuthenticationContext.Provider value={authApi} {...rest}>
      {children}
    </AuthenticationContext.Provider>
  );
};

export const useAuthentication = () => useContext(AuthenticationContext);
